import React, { useState, useEffect, useCallback } from "react";
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { ComponentErrorBoundary } from "../ErrorBoundary";
import Tooltip from "../ui/Tooltip";

/**
 * Form Field Component
 *
 * Renders different types of form inputs based on field configuration.
 * Supports validation, enrichment, and conditional logic.
 */
export default function FormField({
  field,
  value,
  errors = [],
  disabled = false,
  onChange,
  onValidation,
  className = "",
  ...props
}) {
  const [localValue, setLocalValue] = useState(
    value || field.default_value || ""
  );
  const [isFocused, setIsFocused] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichmentData, setEnrichmentData] = useState(null);

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value || field.default_value || "");
  }, [value, field.default_value]);

  // Handle field value changes
  const handleChange = useCallback(
    (newValue) => {
      setLocalValue(newValue);

      // Trigger external change handler
      if (onChange) {
        onChange(newValue);
      }

      // Validate field
      const validationErrors = validateField(field, newValue);
      if (onValidation) {
        onValidation(validationErrors);
      }
    },
    [field, onChange, onValidation]
  );

  // Handle field blur (for enrichment and validation)
  const handleBlur = useCallback(async () => {
    setIsFocused(false);

    // Trigger enrichment if configured
    if (
      field.enrichment?.enabled &&
      field.enrichment.trigger === "on_blur" &&
      localValue
    ) {
      await handleEnrichment(localValue);
    }
  }, [field, localValue]);

  // Handle enrichment
  const handleEnrichment = async (valueToEnrich) => {
    if (!field.enrichment?.enabled || !valueToEnrich) return;

    try {
      setIsEnriching(true);

      // Mock enrichment API call (replace with actual implementation)
      const enrichedData = await enrichField(field, valueToEnrich);

      if (enrichedData) {
        setEnrichmentData(enrichedData);

        // Auto-accept enrichment if configured
        if (field.enrichment.auto_accept) {
          // This would typically update related fields
          console.log("Auto-accepting enriched data:", enrichedData);
        }
      }
    } catch (error) {
      console.error("Enrichment failed:", error);
    } finally {
      setIsEnriching(false);
    }
  };

  // Determine field width class
  const getWidthClass = () => {
    const width = field.config?.width || "full";
    switch (width) {
      case "half":
        return "md:col-span-1";
      case "full":
        return "md:col-span-2";
      case "quarter":
        return "md:col-span-1 lg:col-span-1";
      case "three-quarter":
        return "md:col-span-3";
      default:
        return "md:col-span-1";
    }
  };

  // Render field input based on type
  const renderInput = () => {
    const commonProps = {
      id: field.id,
      name: field.name,
      value: localValue,
      disabled: disabled || isEnriching,
      className: getInputClasses(),
      placeholder: field.placeholder,
      onFocus: () => setIsFocused(true),
      onBlur: handleBlur,
      "aria-describedby": `${field.id}-error ${field.id}-description`,
      "aria-invalid": errors.length > 0,
    };

    switch (field.type) {
      case "text":
        return (
          <input
            type="text"
            {...commonProps}
            onChange={(e) => handleChange(e.target.value)}
            maxLength={field.config?.max_length}
          />
        );

      case "email":
        return (
          <input
            type="email"
            {...commonProps}
            onChange={(e) => handleChange(e.target.value)}
          />
        );

      case "number":
        return (
          <input
            type="number"
            {...commonProps}
            onChange={(e) => handleChange(e.target.value)}
            min={field.config?.min}
            max={field.config?.max}
            step={field.config?.step}
          />
        );

      case "tel":
        return (
          <input
            type="tel"
            {...commonProps}
            onChange={(e) => handleChange(e.target.value)}
          />
        );

      case "url":
        return (
          <input
            type="url"
            {...commonProps}
            onChange={(e) => handleChange(e.target.value)}
          />
        );

      case "password":
        return (
          <input
            type="password"
            {...commonProps}
            onChange={(e) => handleChange(e.target.value)}
          />
        );

      case "postcode":
        return (
          <input
            type="text"
            {...commonProps}
            onChange={(e) => handleChange(e.target.value.toUpperCase())}
            placeholder={field.placeholder || "e.g., SW1A 1AA"}
            maxLength={8}
          />
        );

      case "textarea":
        return (
          <textarea
            {...commonProps}
            onChange={(e) => handleChange(e.target.value)}
            rows={field.config?.rows || 3}
            maxLength={field.config?.max_length}
            className={`${getInputClasses()} resize-vertical`}
          />
        );

      case "select":
        return (
          <select
            {...commonProps}
            onChange={(e) => handleChange(e.target.value)}
            className={getInputClasses()}
          >
            <option value="">Select {field.label}</option>
            {field.config?.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "date":
        return (
          <input
            type="date"
            {...commonProps}
            onChange={(e) => handleChange(e.target.value)}
            min={field.config?.min_date}
            max={field.config?.max_date}
          />
        );

      case "datetime-local":
        return (
          <input
            type="datetime-local"
            {...commonProps}
            onChange={(e) => handleChange(e.target.value)}
          />
        );

      case "checkbox":
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              {...commonProps}
              checked={Boolean(localValue)}
              onChange={(e) => handleChange(e.target.checked)}
              className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor={field.id} className="ml-2 text-sm text-gray-900">
              {field.label}
            </label>
          </div>
        );

      case "radio":
        return (
          <div className="space-y-2">
            {field.config?.options?.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="radio"
                  id={`${field.id}-${option.value}`}
                  name={field.name}
                  value={option.value}
                  checked={localValue === option.value}
                  disabled={disabled}
                  onChange={(e) => handleChange(e.target.value)}
                  className="form-radio h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <label
                  htmlFor={`${field.id}-${option.value}`}
                  className="ml-2 text-sm text-gray-900"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );

      case "file":
        return (
          <input
            type="file"
            {...commonProps}
            onChange={(e) => handleChange(e.target.files?.[0] || null)}
            accept={field.config?.accept}
            multiple={field.config?.multiple}
            className="form-file block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        );

      default:
        return (
          <input
            type="text"
            {...commonProps}
            onChange={(e) => handleChange(e.target.value)}
          />
        );
    }
  };

  // Get input styling classes
  const getInputClasses = () => {
    const baseClasses =
      "block w-full rounded-md border-0 px-3 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 transition-colors";

    if (errors.length > 0) {
      return `${baseClasses} ring-red-300 focus:ring-red-500`;
    }

    if (disabled) {
      return `${baseClasses} bg-gray-50 text-gray-500 ring-gray-200`;
    }

    return baseClasses;
  };

  // Skip rendering label for checkbox (it's handled in the input)
  const showLabel = field.type !== "checkbox";

  return (
    <ComponentErrorBoundary componentName="FormField">
      <div className={`form-field ${getWidthClass()} ${className}`} {...props}>
        {/* Field Label */}
        {showLabel && (
          <div className="flex items-center gap-1 mb-2">
            <label
              htmlFor={field.id}
              className="block text-sm font-medium text-gray-900"
            >
              {field.label}
              {field.required && (
                <span className="text-red-500 ml-1" aria-label="required">
                  *
                </span>
              )}
            </label>
            {field.description && (
              <Tooltip content={field.description} position="top" delay={200}>
                <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
              </Tooltip>
            )}
          </div>
        )}

        {/* Field Input */}
        <div className="relative">
          {renderInput()}

          {/* Enrichment Indicator */}
          {isEnriching && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>

        {/* Enrichment Results */}
        {enrichmentData && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start">
              <CheckCircleIcon className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Address found</p>
                <p className="text-blue-600">
                  {enrichmentData.formatted_address ||
                    "Enriched data available"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Field Errors */}
        {errors.length > 0 && (
          <div
            id={`${field.id}-error`}
            className="mt-2 flex items-start space-x-2"
          >
            <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-600">
              {errors.map((error, index) => (
                <p key={index}>{error}</p>
              ))}
            </div>
          </div>
        )}

        {/* Character Count (for text fields with max_length) */}
        {(field.type === "text" || field.type === "textarea") &&
          field.config?.max_length && (
            <div className="mt-1 text-xs text-gray-500 text-right">
              {localValue.length} / {field.config.max_length}
            </div>
          )}
      </div>
    </ComponentErrorBoundary>
  );
}

/**
 * Validate a field value against its validation rules
 */
function validateField(field, value) {
  const errors = [];

  if (!field.validation) return errors;

  field.validation.forEach((rule) => {
    switch (rule.type) {
      case "required":
        if (!value || (typeof value === "string" && value.trim() === "")) {
          errors.push(rule.message || `${field.label} is required`);
        }
        break;

      case "min_length":
        if (value && value.length < rule.value) {
          errors.push(
            rule.message ||
              `${field.label} must be at least ${rule.value} characters`
          );
        }
        break;

      case "max_length":
        if (value && value.length > rule.value) {
          errors.push(
            rule.message ||
              `${field.label} cannot exceed ${rule.value} characters`
          );
        }
        break;

      case "pattern":
        if (value && !new RegExp(rule.value).test(value)) {
          errors.push(rule.message || `${field.label} format is invalid`);
        }
        break;

      case "email":
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.push(
            rule.message || `${field.label} must be a valid email address`
          );
        }
        break;

      case "url":
        if (value && !/^https?:\/\/.+/.test(value)) {
          errors.push(rule.message || `${field.label} must be a valid URL`);
        }
        break;

      default:
        break;
    }
  });

  return errors;
}

/**
 * Mock enrichment function (replace with actual API calls)
 */
async function enrichField(field, value) {
  if (
    field.enrichment?.source === "postcode_api" &&
    field.type === "postcode"
  ) {
    // Mock postcode enrichment
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          formatted_address: `${value}, London, UK`,
          latitude: 51.5074,
          longitude: -0.1278,
          city: "London",
          region: "Greater London",
        });
      }, 1000);
    });
  }

  return null;
}
